import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  House, CalendarDots, GameController, Trophy, Users, Storefront,
  Medal, UserCircle, Lightning, ChartBar, ArrowLeft, Eye, List, X,
  Books, Broadcast, Rocket, Timer, Megaphone
} from '@phosphor-icons/react';
import GlobalContextSwitcher from '../components/GlobalContextSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { SupportFooter } from '../components/Support';
import { Logo } from '../components/Logo';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { id: 'overview', label: 'Dashboard', icon: House, pathSuffix: '' },
  { id: 'quick-create', label: 'Quick Create', icon: Rocket, pathSuffix: '/quick-create', group: 'ops' },
  { id: 'rsvp-dashboard', label: "Today's RSVPs", icon: Timer, pathSuffix: '/rsvp-dashboard', group: 'ops' },
  { id: 'tournament-ops', label: 'Tournament Ops', icon: Megaphone, pathSuffix: '/tournament-ops', group: 'ops' },
  { id: 'events', label: 'Events', icon: CalendarDots, pathSuffix: '/events', permission: 'shop.events.create' },
  { id: 'open-play', label: 'Open Play', icon: GameController, pathSuffix: '/open-play', permission: 'shop.openplay.create' },
  { id: 'tournaments', label: 'Tournaments', icon: Trophy, pathSuffix: '/tournaments', permission: 'shop.tournaments.manage' },
  { id: 'attendees', label: 'Attendees', icon: Users, pathSuffix: '/attendees', permission: 'shop.rsvps.manage' },
  { id: 'profile', label: 'Shop Profile', icon: Storefront, pathSuffix: '/profile', permission: 'shop.profile.edit' },
  { id: 'badges', label: 'Badges', icon: Medal, pathSuffix: '/badges', permission: 'shop.badges.manage' },
  { id: 'managers', label: 'Managers', icon: UserCircle, pathSuffix: '/managers', permission: 'shop.managers.invite' },
  { id: 'insights', label: 'Insights', icon: ChartBar, pathSuffix: '/insights', permission: 'shop.analytics.view' },
  { id: 'community', label: 'Community', icon: Users, pathSuffix: '/community' },
  { id: 'game-library', label: 'Game Library', icon: Books, pathSuffix: '/game-library' },
  { id: 'rule-packs', label: 'Rule Packs', icon: Books, pathSuffix: '/rule-packs' },
  { id: 'available-tonight', label: 'Available Tonight', icon: Broadcast, pathSuffix: '/available-tonight' },
  { id: 'social-hub', label: 'Social Hub', icon: Megaphone, pathSuffix: '/social-hub' },
  { id: 'plan', label: 'Plan', icon: Lightning, pathSuffix: '/plan', permission: 'shop.plan.manage' },
];

export default function ShopLayout() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useTenant();
  const [shopName, setShopName] = useState('Shop');
  const [tier, setTier] = useState('free');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (shopId) {
      axios.get(`${API_URL}/api/shop-portal/${shopId}/overview`, { withCredentials: true })
        .then(r => {
          setShopName(r.data.shop_name || 'Shop');
          setTier(r.data.subscription_tier || 'free');
        })
        .catch(() => {});
    }
  }, [shopId]);

  const basePath = `/shop/${shopId}`;
  const isActive = (suffix) => {
    const full = basePath + suffix;
    if (suffix === '') return location.pathname === basePath || location.pathname === basePath + '/';
    return location.pathname.startsWith(full);
  };

  const visibleItems = NAV_ITEMS.filter(item =>
    !item.permission || hasPermission(item.permission)
  );

  return (
    <div className="min-h-screen bg-[#06060A] flex" data-testid="shop-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#0a0a12] border-r border-white/[0.06] sticky top-0 h-screen">
        {/* CyBoard brand strip */}
        <div className="px-4 py-2.5 border-b border-white/[0.04]">
          <Logo variant="wordmark" size="xs" clickable={true} />
        </div>
        <div className="px-4 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/home')} className="text-[#555] hover:text-white transition-colors">
              <ArrowLeft size={16} />
            </button>
            <Storefront size={20} weight="duotone" className="text-[#9D4CDD]" />
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate" style={{ fontFamily: 'Unbounded' }}>
                {shopName}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#9D4CDD] font-semibold">
                {tier === 'free' ? 'Free' : 'Pro'} Shop
              </p>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-white/[0.06]">
          <GlobalContextSwitcher variant="sidebar" />
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {visibleItems.map((item, idx) => {
            const prevItem = idx > 0 ? visibleItems[idx - 1] : null;
            const showDivider = prevItem && prevItem.group === 'ops' && item.group !== 'ops';
            return (
              <React.Fragment key={item.id}>
                {showDivider && <div className="mx-4 my-2 border-t border-white/[0.04]" />}
                <button
                  onClick={() => navigate(basePath + item.pathSuffix)}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                    isActive(item.pathSuffix)
                      ? 'text-[#9D4CDD] bg-[#9D4CDD]/[0.06] border-r-2 border-[#9D4CDD]'
                      : 'text-[#888] hover:text-white hover:bg-white/[0.03]'
                  }`}
                  data-testid={`shop-nav-${item.id}`}
                >
                  <item.icon size={18} weight={isActive(item.pathSuffix) ? 'duotone' : 'regular'} />
                  {item.label}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        <button
          onClick={() => navigate(`/shops/${shopId}`)}
          className="flex items-center gap-2 px-5 py-3 text-[#555] hover:text-white text-xs border-t border-white/[0.06] transition-colors"
        >
          <Eye size={14} /> View Public Page
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0a0a12] border-b border-white/[0.06] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/home')} data-testid="shop-back-home"><ArrowLeft size={18} className="text-[#555]" /></button>
            <Storefront size={18} className="text-[#9D4CDD]" />
            <span className="text-white font-semibold text-sm truncate max-w-[150px]">{shopName}</span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="text-[#777]">
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-2"
            >
              {visibleItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { navigate(basePath + item.pathSuffix); setMobileOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
                    isActive(item.pathSuffix)
                      ? 'text-[#9D4CDD] bg-[#9D4CDD]/[0.06]'
                      : 'text-[#777] hover:text-white'
                  }`}
                >
                  <item.icon size={16} weight={isActive(item.pathSuffix) ? 'duotone' : 'regular'} />
                  {item.label}
                </button>
              ))}
              <div className="mt-2 mb-1">
                <GlobalContextSwitcher variant="mobile" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <div className="lg:p-8 p-4 pt-16 lg:pt-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Outlet />
          </motion.div>
          <SupportFooter />
        </div>
      </main>
    </div>
  );
}
